import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Blockly from 'blockly/core';
import 'blockly/blocks';
import * as JavaScript from 'blockly/javascript';
import * as En from 'blockly/msg/en';
import { ArrowLeft, Play, Save, Settings } from 'lucide-react';
import { db } from '../../db/shiksha-db';
import { auth } from '../../services/firebase';
import { MARKING_SCHEMES } from '../../config/marking-schemes';

// Initialize language for Blockly
Blockly.setLocale(En);

// Define custom block for Database Variables
Blockly.Blocks['db_variable'] = {
  init: function() {
    this.appendDummyInput()
        .appendField("Mark:")
        .appendField(new Blockly.FieldDropdown([
          ["Written", "written"], 
          ["Note Book", "notebook"], 
          ["Subject Enrichment", "enrichment"],
          ["Portfolio", "portfolio"],
          ["PT-I", "pt1"],
          ["PT-II", "pt2"],
          ["Pre Mid", "pre_mid"],
          ["Mid Term", "mid"],
          ["Post Mid", "post_mid"],
          ["Multiple Assess", "multiple_assessment"],
          ["Annual", "annual"]
        ]), "VAR_NAME");
    this.setOutput(true, "Number");
    this.setColour(230);
    this.setTooltip("Get a mark value");
  }
};

Blockly.Blocks['best_of_n'] = {
  init: function() {
    this.appendValueInput("A").setCheck("Number").appendField("Best of");
    this.appendValueInput("B").setCheck("Number").appendField("and");
    this.appendValueInput("C").setCheck("Number").appendField("and (opt)");
    this.setOutput(true, "Number");
    this.setColour(230);
    this.setTooltip("Returns the highest value among the inputs");
  }
};

Blockly.Blocks['best_percent_of'] = {
  init: function() {
    this.appendDummyInput().appendField("Best").appendField(new Blockly.FieldNumber(50), "PERCENT").appendField("% of");
    this.appendValueInput("A").setCheck("Number");
    this.appendValueInput("B").setCheck("Number");
    this.appendValueInput("C").setCheck("Number");
    this.setOutput(true, "Number");
    this.setColour(230);
    this.setTooltip("Sorts the values, takes best 2 (or all if <3), averages them, and returns PERCENT of that average.");
  }
};

Blockly.Blocks['percentage'] = {
  init: function() {
    this.appendValueInput("VAL").setCheck("Number").appendField("Calculate");
    this.appendValueInput("OUT_OF").setCheck("Number").appendField("out of");
    this.appendDummyInput().appendField("as %");
    this.setOutput(true, "Number");
    this.setColour(230);
    this.setTooltip("Returns (VAL / OUT_OF) * 100");
  }
};

Blockly.Blocks['grade_assign'] = {
  init: function() {
    this.appendValueInput("SCORE").setCheck("Number").appendField("If score >=");
    this.appendDummyInput().appendField("then grade").appendField(new Blockly.FieldTextInput("A1"), "GRADE");
    this.setPreviousStatement(true, null);
    this.setNextStatement(true, null);
    this.setColour(160);
    this.setTooltip("Assigns a grade if the condition is met");
  }
};

JavaScript.javascriptGenerator.forBlock['db_variable'] = function(block) {
  const dropdown_var_name = block.getFieldValue('VAR_NAME');
  const code = `marks['${dropdown_var_name}'] || 0`;
  return [code, JavaScript.Order.ATOMIC];
};

JavaScript.javascriptGenerator.forBlock['best_of_n'] = function(block) {
  const valA = JavaScript.javascriptGenerator.valueToCode(block, 'A', JavaScript.Order.NONE) || '0';
  const valB = JavaScript.javascriptGenerator.valueToCode(block, 'B', JavaScript.Order.NONE) || '0';
  const valC = JavaScript.javascriptGenerator.valueToCode(block, 'C', JavaScript.Order.NONE);
  const code = valC ? `Math.max(${valA}, ${valB}, ${valC})` : `Math.max(${valA}, ${valB})`;
  return [code, JavaScript.Order.FUNCTION_CALL];
};

JavaScript.javascriptGenerator.forBlock['best_percent_of'] = function(block) {
  const pct = block.getFieldValue('PERCENT');
  const valA = JavaScript.javascriptGenerator.valueToCode(block, 'A', JavaScript.Order.NONE) || '0';
  const valB = JavaScript.javascriptGenerator.valueToCode(block, 'B', JavaScript.Order.NONE) || '0';
  const valC = JavaScript.javascriptGenerator.valueToCode(block, 'C', JavaScript.Order.NONE) || '0';
  
  const code = `(function() {
    const vals = [${valA}, ${valB}, ${valC}].sort((a,b) => b-a);
    const bestTwoAvg = (vals[0] + vals[1]) / 2;
    return bestTwoAvg * (${pct} / 100);
  })()`;
  return [code, JavaScript.Order.FUNCTION_CALL];
};

JavaScript.javascriptGenerator.forBlock['percentage'] = function(block) {
  const val = JavaScript.javascriptGenerator.valueToCode(block, 'VAL', JavaScript.Order.NONE) || '0';
  const outOf = JavaScript.javascriptGenerator.valueToCode(block, 'OUT_OF', JavaScript.Order.NONE) || '1';
  const code = `((${val} / ${outOf}) * 100)`;
  return [code, JavaScript.Order.MULTIPLICATIVE];
};

JavaScript.javascriptGenerator.forBlock['grade_assign'] = function(block) {
  const score = JavaScript.javascriptGenerator.valueToCode(block, 'SCORE', JavaScript.Order.NONE) || '0';
  const grade = block.getFieldValue('GRADE');
  return `if (total >= ${score}) return '${grade}';\n`;
};

const TOOLBOX_XML = `
<xml xmlns="https://developers.google.com/blockly/xml">
  <category name="Variables" colour="#5b80a5">
    <block type="db_variable"></block>
  </category>
  <category name="Math" colour="#5b67a5">
    <block type="math_number">
      <field name="NUM">0</field>
    </block>
    <block type="math_arithmetic">
      <field name="OP">ADD</field>
      <value name="A"><shadow type="math_number"><field name="NUM">1</field></shadow></value>
      <value name="B"><shadow type="math_number"><field name="NUM">1</field></shadow></value>
    </block>
    <block type="math_round"></block>
    <block type="best_of_n"></block>
    <block type="best_percent_of"></block>
    <block type="percentage"></block>
  </category>
  <category name="Logical" colour="#5ba55b">
    <block type="logic_compare"></block>
    <block type="logic_operation"></block>
    <block type="logic_boolean"></block>
    <block type="controls_if"></block>
  </category>
  <category name="Grading" colour="#a55b80">
    <block type="grade_assign"></block>
  </category>
</xml>
`;

export function BlockForge() {
  const blocklyDiv = useRef(null);
  const workspace = useRef(null);
  const [cohort, setCohort] = useState('Class VI');
  const [activeTab, setActiveTab] = useState('formula');
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Dummy data for testing
  const [dummyMarks, setDummyMarks] = useState({});

  useEffect(() => {
    if (blocklyDiv.current && !workspace.current) {
      workspace.current = Blockly.inject(blocklyDiv.current, {
        toolbox: TOOLBOX_XML,
        scrollbars: true,
        trashcan: true
      });
      
      // Load a default workspace
      const defaultXml = MARKING_SCHEMES[cohort]?.defaultXml || '<xml></xml>';
      Blockly.Xml.domToWorkspace(Blockly.utils.xml.textToDom(defaultXml), workspace.current);
    }
    
    return () => {
      // Cleanup happens automatically, but we can clear workspace ref
    };
  }, []);

  const handleTestFormula = () => {
    try {
      const code = JavaScript.javascriptGenerator.workspaceToCode(workspace.current);
      // We wrap the generated code to evaluate it
      // Since it's blockly generating an expression, we can wrap it in a function
      // In this specific setup, we just need the expression. But sometimes it might be statements.
      // Let's assume it evaluates to a number expression if they connect it to nothing, 
      // but if there are multiple blocks, this might be tricky.
      // For simplicity, we just eval the code with the dummy marks in scope.
      
      // Try to get the root block's output
      const topBlocks = workspace.current.getTopBlocks(true);
      if (topBlocks.length === 0) {
        setTestResult("No blocks found.");
        return;
      }
      
      JavaScript.javascriptGenerator.init(workspace.current);
      const rootBlock = topBlocks[0];
      const blockCode = JavaScript.javascriptGenerator.blockToCode(rootBlock);
      const expressionCode = Array.isArray(blockCode) ? blockCode[0] : blockCode;
      
      // Safe-ish eval using Function constructor
      // Security: strip DOM access, loop keywords, and curly braces to prevent
      // infinite loop CPU-exhaustion DoS (per Worker Sandbox Hardening rules)
      const safeCode = (expressionCode || '0')
        .replace(/window|document|localStorage|sessionStorage|fetch|xmlhttprequest|eval/gi, '')
        .replace(/\bwhile\b|\bfor\b|\bdo\b/gi, '')
        .replace(/[{}]/g, '');
      const calculate = new Function('marks', `
        try {
          const result = ${safeCode};
          return typeof result === 'number' ? result : Number(result) || 0;
        } catch(e) {
          return 0;
        }
      `);
      const testScore = calculate(dummyMarks);
      
      setTestResult(testScore);
    } catch (e) {
      setTestResult(`Error: ${e.message}`);
    }
    setTestModalOpen(true);
  };

  const handleCompileAndSave = async () => {
    try {
      const code = JavaScript.javascriptGenerator.workspaceToCode(workspace.current);
      const xml = Blockly.Xml.domToText(Blockly.Xml.workspaceToDom(workspace.current));
      
      const existing = await db.schemas.where('cohort').equals(cohort).first();
      
      if (existing) {
        await db.schemas.update(existing.id, { xml, code, updatedAt: Date.now() });
      } else {
        await db.schemas.add({ cohort, xml, code, updatedAt: Date.now() });
      }

      // Sync to Google Sheets via Cloudflare Worker
      if (auth.currentUser) {
        const idToken = await auth.currentUser.getIdToken(false);
        const API_URL = import.meta.env.VITE_WORKER_API_URL || 'https://shiksha-edge-api.workers.dev';
        const res = await fetch(`${API_URL}/api/schemas/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify({ cohort, xml, code })
        });
        if (!res.ok) {
          throw new Error('Failed to sync to Master Sheet. Will retry on next poll.');
        }
      }
      
      alert("Formula compiled and saved to Master Sheet!");
    } catch (err) {
      alert(`Error saving schema: ${err.message}`);
    }
  };

  // Load saved schema on cohort switch
  useEffect(() => {
    async function loadSchema() {
      // Initialize dummy marks from schema
      const schemaConfig = MARKING_SCHEMES[cohort] || MARKING_SCHEMES['Class VI'];
      const initialMarks = {};
      if (schemaConfig && schemaConfig.columns) {
        Object.values(schemaConfig.columns).forEach(termCols => {
          termCols.forEach(col => {
            initialMarks[col.id] = col.max;
          });
        });
      }
      setDummyMarks(initialMarks);

      if (workspace.current) {
        try {
          const schema = await db.schemas.where('cohort').equals(cohort).first();
          if (schema && schema.xml) {
            workspace.current.clear();
            Blockly.Xml.domToWorkspace(Blockly.utils.xml.textToDom(schema.xml), workspace.current);
          } else {
            workspace.current.clear();
            const defaultXml = MARKING_SCHEMES[cohort]?.defaultXml || '<xml></xml>';
            Blockly.Xml.domToWorkspace(Blockly.utils.xml.textToDom(defaultXml), workspace.current);
          }
        } catch (err) {
          console.error("Error loading schema", err);
        }
      }
    }
    loadSchema();
  }, [cohort]);

  return (
    <div className="min-h-screen bg-paper pb-8 flex flex-col">
      <div className="max-w-6xl mx-auto px-4 py-6 w-full flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="p-2 rounded-lg bg-white border border-divider hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft size={20} className="text-ink" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-ink">Block Forge</h1>
              <p className="text-sm text-graphite font-secondary">Visual AST formula builder.</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={cohort}
              onChange={(e) => setCohort(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            >
              <option value="Class VI">Class VI</option>
              <option value="Class VII">Class VII</option>
              <option value="Class VIII">Class VIII</option>
              <option value="Class IX">Class IX</option>
              <option value="Class X">Class X</option>
            </select>
            {activeTab === 'formula' && (
              <>
                <button
                  onClick={handleTestFormula}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 flex items-center gap-2"
                >
                  <Play size={16} />
                  Test Formula
                </button>
                <button
                  onClick={handleCompileAndSave}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 flex items-center gap-2"
                >
                  <Save size={16} />
                  Compile & Save
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex border-b border-gray-200 mb-4">
          <button 
            className={`py-2 px-4 font-medium text-sm transition-colors ${activeTab === 'formula' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} 
            onClick={() => setActiveTab('formula')}
          >
            Formula Builder
          </button>
          <button 
            className={`py-2 px-4 font-medium text-sm transition-colors ${activeTab === 'exams' ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} 
            onClick={() => setActiveTab('exams')}
          >
            Exams & Max Marks
          </button>
        </div>

        {/* ── Main Content ── */}
        <div className={`flex-1 bg-white border border-divider rounded-xl overflow-hidden shadow-sm min-h-[500px] relative ${activeTab === 'formula' ? 'flex' : 'hidden'}`}>
          <div ref={blocklyDiv} className="absolute inset-0" />
        </div>

        {activeTab === 'exams' && (
          <div className="flex-1 bg-white border border-divider rounded-xl p-6 shadow-sm overflow-y-auto min-h-[500px]">
            <h2 className="text-xl font-bold text-ink mb-6">Exams for {cohort}</h2>
            {MARKING_SCHEMES[cohort] && Object.entries(MARKING_SCHEMES[cohort].columns).map(([term, exams]) => (
              <div key={term} className="mb-8">
                <h3 className="text-lg font-semibold text-graphite mb-4">{term}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-3 px-4 text-sm font-semibold text-gray-600">ID</th>
                        <th className="py-3 px-4 text-sm font-semibold text-gray-600">Label</th>
                        <th className="py-3 px-4 text-sm font-semibold text-gray-600">Maximum Marks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exams.map(exam => (
                        <tr key={exam.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 text-sm text-gray-800 font-mono">{exam.id}</td>
                          <td className="py-3 px-4 text-sm text-gray-800">{exam.label}</td>
                          <td className="py-3 px-4 text-sm font-bold text-indigo-600">{exam.max}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Test Modal ── */}
      {testModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-ink flex items-center gap-2">
                <Settings size={20} className="text-indigo-600" />
                Test Formula Math
              </h3>
              <button 
                onClick={() => setTestModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              <p className="text-sm text-graphite font-secondary">Modify dummy marks to test your formula output.</p>
              
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(dummyMarks).map(([key, val]) => (
                  <div key={key}>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">
                      {key}
                    </label>
                    <input 
                      type="number"
                      value={val}
                      onChange={(e) => setDummyMarks({...dummyMarks, [key]: Number(e.target.value)})}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-indigo-500"
                    />
                  </div>
                ))}
              </div>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4 flex items-center justify-between">
                <span className="text-sm font-bold text-gray-700">Calculated Result:</span>
                <span className="text-2xl font-black text-indigo-600">{testResult}</span>
              </div>
            </div>
            
            <button
              onClick={handleTestFormula}
              className="w-full py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Re-Calculate
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
